import type { DeepPartial } from "react-hook-form";
import { makeAssistantToolUI, useMessage } from "@assistant-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CornerDownLeft,
  FlaskConicalOff,
  Loader,
} from "lucide-react";

import type {
  CodemodScriptResponse,
  CodeSearchResponse,
  CodeTransformationResponse,
} from "@acme/modern-ai";
import type { SupportedLang } from "@acme/validators";
import { Alert, AlertDescription } from "@acme/ui/alert";
import { CodeHeader } from "@acme/ui/assistant-ui/markdown-text";
import { Button } from "@acme/ui/button";
import { CodeHighlightWithMatches } from "@acme/ui/codehike/code-highlight-with-matches";
import { CodeHighlighter } from "@acme/ui/codehike/code-highlighter";
import { CodeTransitionWithHighlighter } from "@acme/ui/codehike/code-transition-with-highlighter";
import { StudioMode } from "@acme/validators";

import { useStudioStore } from "../_hooks/use-studio-store";

function renderFailed() {
  return (
    <Alert variant="default" className="my-2 p-2">
      <AlertDescription className="flex items-center gap-2">
        <AlertCircle className="size-4" />
        The rule did not pass all the tests.
      </AlertDescription>
    </Alert>
  );
}

export const AstGrepSearchRuleResultToolUI = makeAssistantToolUI<
  DeepPartial<CodeSearchResponse>,
  string
>({
  toolName: "ast_grep_search_rule_validator",
  render: (result) => {
    const complete = !!result.result;
    const passed = result.result === "All tests passed";
    if (result.args.tests && !Array.isArray(result.args.tests)) {
      result.args.tests = [];
    }
    return (
      <CodeSearchResult
        result={result.args}
        passed={passed}
        complete={complete}
        toolCallId={result.toolCallId}
      />
    );
  },
});

export const AstGrepCodeTransformationRuleResultToolUI = makeAssistantToolUI<
  DeepPartial<CodeTransformationResponse>,
  string
>({
  toolName: "ast_grep_code_transformation_rule_validator",
  render: (result) => {
    const complete = !!result.result;
    const passed = result.result === "All tests passed";
    if (result.args.tests && !Array.isArray(result.args.tests)) {
      result.args.tests = [];
    }
    return (
      <CodeTransformationResult
        result={result.args}
        passed={passed}
        complete={complete}
        toolCallId={result.toolCallId}
      />
    );
  },
});

export const AstGrepCodemodScriptResultToolUI = makeAssistantToolUI<
  DeepPartial<CodemodScriptResponse>,
  string
>({
  toolName: "ast_grep_codemod_script_validator",
  render: (result) => {
    const complete = !!result.result;
    const passed = result.result === "All tests passed";
    if (result.args.tests && !Array.isArray(result.args.tests)) {
      result.args.tests = [];
    }
    return (
      <CodemodScriptResult
        result={result.args}
        passed={passed}
        complete={complete}
        toolCallId={result.toolCallId}
      />
    );
  },
});

export function CodeTransformationResult({
  result,
  passed,
  complete,
  toolCallId,
}: {
  result: DeepPartial<CodeTransformationResponse>;
  passed: boolean;
  complete: boolean;
  toolCallId: string;
}) {
  const lang = useStudioStore((state) => state.lang);
  const setMode = useStudioStore((state) => state.setMode);
  const setConfig = useStudioStore((state) => state.setConfig);
  const addAutoGeneratedSources = useStudioStore(
    (state) => state.addAutoGeneratedSources,
  );
  const isLastToolCall = useIsLastToolCall(toolCallId);

  const handleAcceptWithoutTests = () => {
    setMode(StudioMode.Config);
    setConfig(result.rule ?? "");
  };

  const handleAccept = () => {
    addAutoGeneratedSources(
      result.tests?.map((test, index) => ({
        id: index,
        autoGenerated: true,
        source: test?.beforeCode ?? "",
      })) ?? [],
    );
    handleAcceptWithoutTests();
  };

  const shouldShowApplyButtons = complete && (passed || isLastToolCall);
  const shouldShowError = complete && !passed && !isLastToolCall;

  return (
    <AnimatePresence mode="wait" initial={false}>
      {result.rule && (
        <motion.div
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="overflow-hidden py-2"
          key="transformation-result"
        >
          <div className="flex w-full flex-col">
            <CodeHeader
              language="yaml"
              code={result.rule ?? ""}
              title="ast-grep rule"
            />
            <CodeHighlighter language="yaml" code={result.rule ?? ""} />
          </div>

          <h3 className="my-2">Test cases:</h3>
          <div className="space-y-2">
            {result.tests?.map((test, index) => (
              <CodeTransitionWithHighlighter
                key={index}
                before={test?.beforeCode ?? ""}
                after={test?.afterCode ?? ""}
                title={`Test ${index + 1}`}
                index={index}
                language={lang}
              />
            ))}
          </div>
          {shouldShowApplyButtons && (
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="default"
                className="inline-flex items-center gap-1.5 font-medium"
                onClick={handleAccept}
                size="sm"
                type="button"
              >
                Apply
                <CornerDownLeft className="size-3" />
              </Button>
              <Button
                variant="outline"
                className="dark:border-primary dark:text-primary inline-flex items-center gap-1.5 font-medium"
                onClick={handleAcceptWithoutTests}
                size="sm"
                type="button"
              >
                Apply without tests
                <FlaskConicalOff className="size-3" />
              </Button>
            </div>
          )}
          {shouldShowError && renderFailed()}
          {!complete && <AIToolLoadingAlert />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function CodemodScriptResult({
  result,
  passed,
  complete,
  toolCallId,
}: {
  result: DeepPartial<CodemodScriptResponse>;
  passed: boolean;
  complete: boolean;
  toolCallId: string;
}) {
  const setMode = useStudioStore((state) => state.setMode);
  const lang = useStudioStore((state) => state.lang);
  const setJsAstGrepCodemod = useStudioStore(
    (state) => state.setJsAstGrepCodemod,
  );
  const addAutoGeneratedSources = useStudioStore(
    (state) => state.addAutoGeneratedSources,
  );
  const isLastToolCall = useIsLastToolCall(toolCallId);

  const handleAcceptWithoutTests = () => {
    setMode(StudioMode.JsAstGrep);
    setJsAstGrepCodemod(result.code ?? "");
  };

  const handleAccept = () => {
    addAutoGeneratedSources(
      result.tests?.map((test, index) => ({
        id: index,
        autoGenerated: true,
        source: test?.beforeCode ?? "",
      })) ?? [],
    );
    handleAcceptWithoutTests();
  };

  const shouldShowApplyButtons = complete && (passed || isLastToolCall);
  const shouldShowError = complete && !passed && !isLastToolCall;

  return (
    <AnimatePresence mode="wait" initial={false}>
      {result.code && (
        <motion.div
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="overflow-hidden py-2"
          key="transformation-result"
        >
          <div className="flex w-full flex-col">
            <CodeHeader
              language="typescript"
              code={result.code ?? ""}
              title="Codemod"
            />
            <CodeHighlighter language="typescript" code={result.code ?? ""} />
          </div>

          <h3 className="my-2">Test cases:</h3>
          <div className="space-y-2">
            {result.tests?.map((test, index) => (
              <CodeTransitionWithHighlighter
                key={index}
                before={test?.beforeCode ?? ""}
                after={test?.afterCode ?? ""}
                title={`Test ${index + 1}`}
                index={index}
                language={lang}
              />
            ))}
          </div>
          {shouldShowApplyButtons && (
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="default"
                className="inline-flex items-center gap-1.5 font-medium"
                onClick={handleAccept}
                size="sm"
                type="button"
              >
                Apply
                <CornerDownLeft className="size-3" />
              </Button>
              <Button
                variant="outline"
                className="dark:border-primary dark:text-primary inline-flex items-center gap-1.5 font-medium"
                onClick={handleAcceptWithoutTests}
                size="sm"
                type="button"
              >
                Apply without tests
                <FlaskConicalOff className="size-3" />
              </Button>
            </div>
          )}
          {shouldShowError && renderFailed()}
          {!complete && <AIToolLoadingAlert />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function CodeSearchResult({
  result,
  passed,
  complete,
  toolCallId,
}: {
  result: DeepPartial<CodeSearchResponse>;
  passed: boolean;
  complete: boolean;
  toolCallId: string;
}) {
  const isLastToolCall = useIsLastToolCall(toolCallId);

  const setMode = useStudioStore((state) => state.setMode);
  const setConfig = useStudioStore((state) => state.setConfig);
  const setLang = useStudioStore((state) => state.overrideLang);
  const addAutoGeneratedSources = useStudioStore(
    (state) => state.addAutoGeneratedSources,
  );

  const handleAcceptWithoutTests = () => {
    setMode(StudioMode.Config);
    setConfig(result.rule ?? "");
    setLang(result.language as unknown as SupportedLang);
  };

  const handleAccept = () => {
    addAutoGeneratedSources(
      result.tests?.map((test, index) => ({
        id: index,
        autoGenerated: true,
        source: test?.sourceCode ?? "",
      })) ?? [],
    );
    handleAcceptWithoutTests();
  };

  const shouldShowApplyButtons = complete && (passed || isLastToolCall);
  const shouldShowError = complete && !passed && !isLastToolCall;

  return (
    <AnimatePresence mode="wait" initial={false}>
      {result.rule && (
        <motion.div
          className="py-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          key="search-result"
        >
          <div className="flex w-full flex-col">
            <CodeHeader
              language="yaml"
              code={result.rule}
              title="ast-grep rule"
            />
            <CodeHighlighter language="yaml" code={result.rule} />
          </div>
          <h3 className="my-2">Test cases:</h3>
          <div className="space-y-2">
            {result.tests?.map((test, index) => {
              return (
                <CodeHighlightWithMatches
                  code={test?.sourceCode ?? ""}
                  matches={
                    test?.expectedMatches?.filter((v) => v !== undefined) ?? []
                  }
                  key={index}
                />
              );
            })}
          </div>
          {shouldShowApplyButtons && (
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="default"
                className="inline-flex items-center gap-1.5 font-medium"
                onClick={handleAccept}
                size="sm"
                type="button"
              >
                Apply
                <CornerDownLeft className="size-3" />
              </Button>
              <Button
                variant="outline"
                className="dark:border-primary dark:text-primary inline-flex items-center gap-1.5 font-medium"
                onClick={handleAcceptWithoutTests}
                size="sm"
                type="button"
              >
                Apply without tests
                <FlaskConicalOff className="size-3" />
              </Button>
            </div>
          )}
          {shouldShowError && renderFailed()}
          {!complete && <AIToolLoadingAlert />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AIToolLoadingAlert({ message }: { message?: string }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        key="testing-output"
      >
        <Alert variant="default" className="my-2 p-2">
          <AlertDescription className="flex items-center gap-2">
            <Loader className="size-4 animate-spin" />
            <span
              className="animate-shine text-foreground/60 bg-clip-text"
              style={{
                backgroundImage:
                  "linear-gradient(120deg, rgba(255, 255, 255, 0) 40%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 0) 60%)",
                backgroundSize: "200% 100%",
                WebkitBackgroundClip: "text",
              }}
            >
              {message ?? "Testing the output..."}
            </span>
          </AlertDescription>
        </Alert>
      </motion.div>
    </AnimatePresence>
  );
}

function useIsLastToolCall(toolCallId: string) {
  const message = useMessage();
  for (let i = message.content.length - 1; i >= 0; i--) {
    const part = message.content[i];
    if (part?.type === "tool-call") {
      return part.toolCallId === toolCallId;
    }
  }
  return false;
}
